import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  isStaleChunkError,
  reloadOnceForStaleChunk,
} from '@/shared/utils/staleChunk';

/**
 * Drop-in replacement for React.lazy that recovers from stale chunks left over
 * by a frontend deploy (SP-1330).
 *
 * When the dynamic import fails because the hashed file is gone, we trigger one
 * fresh document load and keep the import promise pending, so the user sees the
 * normal Suspense fallback until the new bundle takes over instead of the error
 * boundary. Any other failure is rethrown untouched.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own constraint
export const lazyWithReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> =>
  lazy(() =>
    factory().catch((error: unknown) => {
      if (isStaleChunkError(error) && reloadOnceForStaleChunk()) {
        // The document is being replaced; never settle this import.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    })
  );
